import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  RunFilters,
  RunStatus,
  MonitoringMode,
  TriggerHistoryFilters,
  TriggerHistoryStatus,
} from '@/types/monitoring';
import { defaultFilters, defaultTriggerHistoryFilters } from '@/types/monitoring';

export type LayoutOrientation = 'horizontal' | 'vertical';

type MonitoringState = {
  // Selection state
  selectedRunId: string | null;
  selectedExecutionId: string | null;
  selectedNodeId: string | null;

  // Trigger history selection state. Independent from run selection because
  // the right-hand detail panel swaps between the two depending on
  // `monitoringMode`, so a previously-selected run stays remembered when the
  // user toggles to Triggers and back.
  selectedTriggerName: string | null;
  selectedTriggerHistoryId: string | null;

  // Which sub-view of the Monitoring tab is active.
  monitoringMode: MonitoringMode;

  // UI state
  sidebarCollapsed: boolean;
  autoRefreshEnabled: boolean;
  autoRefreshInterval: number; // milliseconds
  actionPanelOpen: boolean;
  layoutOrientation: LayoutOrientation;

  // Filters (persisted)
  filters: RunFilters;
  triggerHistoryFilters: TriggerHistoryFilters;
};

type MonitoringActions = {
  // Selection actions
  setSelectedRunId: (runId: string | null) => void;
  setSelectedExecutionId: (executionId: string | null) => void;
  setSelectedNodeId: (nodeId: string | null) => void;
  clearSelection: () => void;

  // Trigger history selection actions
  setSelectedTriggerName: (triggerName: string | null) => void;
  setSelectedTriggerHistoryId: (historyId: string | null) => void;

  // Mode actions
  setMonitoringMode: (mode: MonitoringMode) => void;

  // UI actions
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleAutoRefresh: () => void;
  setAutoRefreshEnabled: (enabled: boolean) => void;
  setAutoRefreshInterval: (interval: number) => void;
  setActionPanelOpen: (open: boolean) => void;
  setLayoutOrientation: (orientation: LayoutOrientation) => void;

  // Filter actions
  setFilters: (filters: Partial<RunFilters>) => void;
  setStatusFilter: (statuses: RunStatus[]) => void;
  setWorkflowNameFilter: (workflowName: string | undefined) => void;
  setDateRangeFilter: (start: string | undefined, end: string | undefined) => void;
  setDurationRangeFilter: (min: number | undefined, max: number | undefined) => void;
  setSearchQuery: (query: string | undefined) => void;
  clearFilters: () => void;

  // Trigger history filter actions
  setTriggerHistoryFilters: (filters: Partial<TriggerHistoryFilters>) => void;
  setTriggerHistoryStatusFilter: (statuses: TriggerHistoryStatus[]) => void;
  clearTriggerHistoryFilters: () => void;
};

type MonitoringStore = MonitoringState & MonitoringActions;

export const useMonitoringStore = create<MonitoringStore>()(
  persist(
    (set) => ({
      // Initial state
      selectedRunId: null,
      selectedExecutionId: null,
      selectedNodeId: null,
      selectedTriggerName: null,
      selectedTriggerHistoryId: null,
      monitoringMode: 'runs',
      sidebarCollapsed: false,
      // Auto-refresh is on by default so newly produced runs (e.g. from a
      // recurrence trigger) surface on the monitoring view without a manual
      // page refresh.
      autoRefreshEnabled: true,
      autoRefreshInterval: 30000, // 30 seconds
      actionPanelOpen: false,
      layoutOrientation: 'horizontal',
      filters: defaultFilters,
      triggerHistoryFilters: defaultTriggerHistoryFilters,

      // Selection actions
      setSelectedRunId: (runId) =>
        set({
          selectedRunId: runId,
          selectedExecutionId: null,
          selectedNodeId: null,
          actionPanelOpen: false,
        }),

      setSelectedExecutionId: (executionId) =>
        set({
          selectedExecutionId: executionId,
          selectedNodeId: null,
          actionPanelOpen: false,
        }),

      setSelectedNodeId: (nodeId) =>
        set({
          selectedNodeId: nodeId,
          actionPanelOpen: nodeId !== null,
        }),

      clearSelection: () =>
        set({
          selectedRunId: null,
          selectedExecutionId: null,
          selectedNodeId: null,
          actionPanelOpen: false,
          // Also clear trigger-history selection so switching workflows
          // does not surface a stale Trigger History detail panel that
          // belongs to a different workflow.
          selectedTriggerName: null,
          selectedTriggerHistoryId: null,
        }),

      // Trigger history selection actions
      setSelectedTriggerName: (triggerName) =>
        set({
          selectedTriggerName: triggerName,
          // Clearing the active trigger also clears the selected row to avoid
          // an orphaned id that no longer matches the visible list.
          selectedTriggerHistoryId: null,
        }),

      setSelectedTriggerHistoryId: (historyId) =>
        set({ selectedTriggerHistoryId: historyId }),

      // Mode actions
      setMonitoringMode: (mode) => set({ monitoringMode: mode }),

      // UI actions
      toggleSidebar: () =>
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

      setSidebarCollapsed: (collapsed) =>
        set({ sidebarCollapsed: collapsed }),

      toggleAutoRefresh: () =>
        set((state) => ({ autoRefreshEnabled: !state.autoRefreshEnabled })),

      setAutoRefreshEnabled: (enabled) =>
        set({ autoRefreshEnabled: enabled }),

      setAutoRefreshInterval: (interval) =>
        set({ autoRefreshInterval: interval }),

      setActionPanelOpen: (open) =>
        set((state) => ({
          actionPanelOpen: open,
          selectedNodeId: open ? state.selectedNodeId : null,
        })),

      setLayoutOrientation: (orientation) =>
        set({ layoutOrientation: orientation }),

      // Filter actions
      setFilters: (newFilters) =>
        set((state) => ({
          filters: { ...state.filters, ...newFilters },
        })),

      setStatusFilter: (statuses) =>
        set((state) => ({
          filters: { ...state.filters, statuses },
        })),

      setWorkflowNameFilter: (workflowName) =>
        set((state) => ({
          filters: { ...state.filters, workflowName },
        })),

      setDateRangeFilter: (start, end) =>
        set((state) => ({
          filters: {
            ...state.filters,
            dateRange: start || end ? { start, end } : undefined,
          },
        })),

      setDurationRangeFilter: (min, max) =>
        set((state) => ({
          filters: {
            ...state.filters,
            durationRange: min !== undefined || max !== undefined ? { min, max } : undefined,
          },
        })),

      setSearchQuery: (query) =>
        set((state) => ({
          filters: { ...state.filters, searchQuery: query },
        })),

      clearFilters: () =>
        set({ filters: defaultFilters }),

      // Trigger history filter actions
      setTriggerHistoryFilters: (newFilters) =>
        set((state) => ({
          triggerHistoryFilters: { ...state.triggerHistoryFilters, ...newFilters },
        })),

      setTriggerHistoryStatusFilter: (statuses) =>
        set((state) => ({
          triggerHistoryFilters: { ...state.triggerHistoryFilters, statuses },
        })),

      clearTriggerHistoryFilters: () =>
        set({ triggerHistoryFilters: defaultTriggerHistoryFilters }),
    }),
    {
      name: 'monitoring-store',
      partialize: (state) => ({
        // Only persist these fields
        filters: state.filters,
        triggerHistoryFilters: state.triggerHistoryFilters,
        monitoringMode: state.monitoringMode,
        autoRefreshInterval: state.autoRefreshInterval,
        sidebarCollapsed: state.sidebarCollapsed,
        layoutOrientation: state.layoutOrientation,
      }),
    }
  )
);
